import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Divider } from '@mui/material';
import { dictionaryService } from '../services/api';

// Reserve aux utilisateurs connectes (admin/super_admin/relecteur) : contrairement au formulaire
// "suggerer un mot" (public, pour un mot absent), ceci corrige une entree EXISTANTE. Les champs sont
// pre-remplis avec le texte actuel : on change ce qui est faux, on envoie. Rien n'est modifie directement,
// chaque champ change devient une proposition que le super admin valide (page « Corrections dictionnaire »).
// L'allemand (texte original de 1914) n'est volontairement pas proposé.
const FIELDS = [
  { key: 'grammar', label: 'Grammaire', multiline: false },
  { key: 'french', label: 'Français', multiline: true },
  { key: 'english', label: 'Anglais', multiline: true },
  { key: 'spanish', label: 'Espagnol', multiline: true },
  { key: 'note', label: 'Note', multiline: true },
];
const EXAMPLE_FIELDS = [
  { key: 'duala', label: 'Duala' },
  { key: 'francais', label: 'Français' },
  { key: 'anglais', label: 'Anglais' },
  { key: 'espagnol', label: 'Espagnol' },
];

const toExamples = (raw) => {
  let v = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch (_) { v = []; } }
  return Array.isArray(v) ? v : [];
};

function ReportCorrectionForm({ entry, onClose }) {
  const initial = {};
  FIELDS.forEach(({ key }) => { initial[key] = entry[key] || ''; });
  const initialExamples = toExamples(entry.examples).map((ex) => ({
    duala: ex.duala || '', francais: ex.francais || '', anglais: ex.anglais || '', espagnol: ex.espagnol || '', allemand: ex.allemand || '',
  }));

  const [values, setValues] = useState(initial);
  const [examples, setExamples] = useState(initialExamples);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const changedFields = FIELDS.filter(({ key }) => values[key].trim() !== initial[key].trim()).map(({ key }) => key);
  const examplesChanged = JSON.stringify(examples) !== JSON.stringify(initialExamples);
  const nothingChanged = changedFields.length === 0 && !examplesChanged;

  const setExample = (i, key, value) => setExamples((prev) => prev.map((ex, idx) => (idx === i ? { ...ex, [key]: value } : ex)));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nothingChanged) return;
    const changes = {};
    changedFields.forEach((key) => { changes[key] = values[key]; });
    if (examplesChanged) changes.examples = examples;
    setSubmitting(true);
    setError('');
    dictionaryService.proposeChanges(entry.id, changes, note.trim())
      .then((res) => setDone(res.data.count))
      .catch((err) => setError((err.response && err.response.data && err.response.data.error) || 'Erreur lors de l\'envoi, réessayez plus tard.'))
      .finally(() => setSubmitting(false));
  };

  if (done) {
    return <Alert severity="success" sx={{ mt: 1.5 }} onClose={onClose}>Merci ! {done} correction(s) transmise(s) pour validation. Le dictionnaire n&apos;est modifié qu&apos;après validation.</Alert>;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ mt: 1.5, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Corriger cette entrée
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Modifiez directement les textes qui sont faux : seuls les champs que vous changez sont envoyés. Le mot duala lui-même ne se corrige pas ici.
      </Typography>
      {FIELDS.map(({ key, label, multiline }) => (
        <TextField
          key={key}
          fullWidth
          size="small"
          label={label}
          value={values[key]}
          onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          sx={{ mb: 1 }}
        />
      ))}
      {examples.length > 0 && (
        <>
          <Divider textAlign="left" sx={{ my: 1.5 }}>Exemples</Divider>
          {examples.map((ex, i) => (
            <Box key={i} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Exemple {i + 1}</Typography>
              {EXAMPLE_FIELDS.map(({ key, label }) => (
                <TextField
                  key={key}
                  fullWidth
                  size="small"
                  label={label}
                  value={ex[key]}
                  onChange={(e) => setExample(i, key, e.target.value)}
                  multiline
                  sx={{ mt: 1 }}
                />
              ))}
            </Box>
          ))}
        </>
      )}
      <TextField
        fullWidth
        size="small"
        label="Précision pour le validateur (optionnel)"
        placeholder="Ex. vérifié dans le livre p. 42"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        multiline
        minRows={2}
        sx={{ mb: 1 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={submitting || nothingChanged}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          Envoyer {nothingChanged ? '' : `(${changedFields.length + (examplesChanged ? 1 : 0)} champ${changedFields.length + (examplesChanged ? 1 : 0) > 1 ? 's' : ''})`}
        </Button>
        <Button size="small" onClick={onClose}>Annuler</Button>
      </Box>
    </Box>
  );
}

export default ReportCorrectionForm;
